import { createSelector } from "reselect";
import { get } from "lodash-es";
import {
  aptosChainList,
  chainList as evmChainList,
  solanaChainList,
  ecosystemList,
} from "features/configure";
import { isVersionLessThan } from "features/helpers/utils";
const selectEntityConfigReducer = (state) => {
  return state.metadesk.entityConfig;
};

const chainName = {
  evm: "EVM",
  aptos: "Aptos",
  solana: "Solana",
};

const chainImages = {
  evm: require("assets/img/ETH_grey_bg.png"),
  aptos: require("assets/img/aptos.png"),
  solana: require("assets/img/solana.png"),
};

const selectTicketTypeReducer = (state) => {
  return state.metadesk.ticketTypeList;
};

export const selectTicketType = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.ticketTypes
);

export const selectTicketTypeForDropdown = createSelector(
  [selectTicketTypeReducer],
  (ticketTypeList) =>
    ticketTypeList.map((ticketType) => {
      return { ...ticketType, value: ticketType._id, label: ticketType.name };
    })
);

export const selectEntityCompany = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.company
);

export const selectEntityCampaign = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.campaign
);

export const selectServiceModule = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.serviceModule
);

export const selectNotificationModule = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.notificationModule
);

export const selectXMTPConnected = createSelector(
  [selectNotificationModule],
  (NotificationModule) => NotificationModule?.xmtp?.connected
);

export const selectKolModule = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.kolModule
);

export const selectCDPModule = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.modules?.CDP
);

export const selectAnalyticsModule = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.analyticsModule
);

export const selectAttributionVersionCanUpdate = createSelector(
  [selectAnalyticsModule],
  (analyticsModule) => {
    if (!analyticsModule) {
      return {
        version: null,
        latestVersion: null,
        canUpdate: false,
      };
    }
    const connected = get(analyticsModule, "attribution.connected");
    return {
      connected,
      version: connected && analyticsModule.attribution?.version,
      latestVersion: analyticsModule.attribution?.latestVersion,
      canUpdate:
        connected &&
        isVersionLessThan(
          analyticsModule.attribution?.version,
          analyticsModule.attribution?.latestVersion
        ),
    };
  }
);

export const selectWidgetModule = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.widgetModule
);

export const selectWidgetVersionCanUpdate = createSelector(
  [selectWidgetModule],
  (widgetModule) => {
    if (!widgetModule) {
      return {
        version: null,
        latestVersion: null,
        canUpdate: false,
      };
    }
    return {
      version: widgetModule.version,
      latestVersion: widgetModule.latestVersion,
      canUpdate: isVersionLessThan(
        widgetModule.version,
        widgetModule.latestVersion
      ),
    };
  }
);

export const selectWidgetConnected = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => {
    return (
      entityConfig?.widgetModule &&
      (entityConfig?.widgetModule.connected ||
        entityConfig?.widgetModule.version)
    );
  }
);

export const selectWebsiteOptIn = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.notificationModule?.websiteOptIn
);

export const selectWebsiteOptInVersionCanUpdate = createSelector(
  [selectWebsiteOptIn],
  (WebsiteOptIn) => {
    if (!WebsiteOptIn) {
      return {
        version: null,
        latestVersion: null,
        canUpdate: false,
      };
    }
    return {
      version: WebsiteOptIn.version,
      latestVersion: WebsiteOptIn.latestVersion,
      canUpdate: isVersionLessThan(
        WebsiteOptIn.version,
        WebsiteOptIn.latestVersion
      ),
    };
  }
);

export const selectWebsiteOptInConnected = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => {
    return (
      entityConfig?.notificationModule.websiteOptIn &&
      (entityConfig?.notificationModule.websiteOptIn.connected ||
        entityConfig?.notificationModule.websiteOptIn.version)
    );
  }
);

export const selectEntityId = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig._id
);

export const selectChainIds = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.chainIds
);

export const selectNftInfo = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.nftInfo || []
);

export const selectTicketTypes = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.ticketTypes || []
);

export const selectDemoSite = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => process.env.REACT_APP_ENV === "demo"
);

export const selectClientSite = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.clientSite
);

export const selectWidgetObject = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.widgetModule || {}
);

export const selectEcosystemName = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => get(chainName, entityConfig.ecosystem, "EVM")
);

export const selectEnabledEcosystems = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) =>
    Object.entries(entityConfig.ecosystems)
      .filter(([_, isEnabled]) => isEnabled)
      .map(([key]) => key)
);

export const selectEcosystemsChainIds = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.ecosystemsChainIds
);

export const selectSubEntities = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => {
    return get(entityConfig, "subEntities", []);
  }
);

export const selectEcosystemImage = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) =>
    get(
      chainImages,
      entityConfig.ecosystem,
      require("assets/img/ETH_grey_bg.png")
    )
);

export const selectEcosystemList = createSelector(
  [selectEntityConfigReducer],
  () => ecosystemList
);

export const selectEntityChainList = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => {
    const { ecosystems } = entityConfig;
    let chainList = [];

    if (ecosystems?.evm) {
      chainList = [...chainList, ...evmChainList];
    }

    if (ecosystems?.aptos) {
      chainList = [...chainList, ...aptosChainList];
    }

    if (ecosystems?.solana) {
      chainList = [...chainList, ...solanaChainList];
    }

    return chainList;
  }
);

export const selectTrackingObject = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) => entityConfig.tracking || {}
);

export const selectSurveyConfig = createSelector(
  [selectEntityConfigReducer],
  (entityConfig) =>
    entityConfig.surveyConfig || {
      isSurveyEnabled: false,
      surveyBtnStyle: "star",
      surveyMsg: "",
    }
);
